import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    Input,
    Button,
    Switch,
    Typography,
} from "@material-tailwind/react";
import { t } from "i18next";

const emptyForm = {
    country_name: "",
    country_code: "",
    currency_code: "",
    currency_name: "",
    currency_symbol: "",
    flag: "",
    exchange_rate: 1,
    is_active: true,
    is_default: false,
    is_manual_rate: false,
};

const getFlag = (countryCode = "") => {
    if (!countryCode || countryCode.length !== 2) {
        return "";
    }

    return countryCode
        .toUpperCase()
        .split("")
        .map((char) =>
            String.fromCodePoint(
                127397 + char.charCodeAt(0)
            )
        )
        .join("");
};

export default function CurrencyForm({
    currency = null,
    onSubmit,
    onCancel,
    loading = false,
}) {
    const [form, setForm] = useState(emptyForm);

    const [errors, setErrors] = useState({});

    const isEdit = Boolean(currency);
    const { t } = useTranslation("currency");
    useEffect(() => {
        if (currency) {
            setForm({
                country_name:
                    currency.country_name || "",

                country_code:
                    currency.country_code || "",

                currency_code:
                    currency.currency_code || "",

                currency_name:
                    currency.currency_name || "",

                currency_symbol:
                    currency.currency_symbol || "",

                flag:
                    currency.flag ||
                    getFlag(currency.country_code),

                exchange_rate:
                    currency.exchange_rate || 1,

                is_active:
                    currency.is_active ?? true,

                is_default:
                    currency.is_default ?? false,

                is_manual_rate:
                    currency.is_manual_rate ?? false,
            });
        } else {
            setForm({ ...emptyForm });
        }

        setErrors({});
    }, [currency]);

    const handleChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const handleCountryCodeChange = (value) => {
        const countryCode =
            value
                .replace(/[^a-zA-Z]/g, "")
                .slice(0, 2)
                .toUpperCase();

        handleChange(
            "country_code",
            countryCode
        );

        handleChange(
            "flag",
            getFlag(countryCode)
        );
    };

    const validate = () => {
        const newErrors = {};

        if (!form.country_name.trim()) {
            newErrors.country_name =
                "Country name is required.";
        }

        if (!form.country_code.trim()) {
            newErrors.country_code =
                "Country code is required.";
        } else if (
            form.country_code.length !== 2
        ) {
            newErrors.country_code =
                "Country code must be 2 characters.";
        }

        if (!form.currency_code.trim()) {
            newErrors.currency_code =
                "Currency code is required.";
        } else if (
            form.currency_code.length !== 3
        ) {
            newErrors.currency_code =
                "Currency code must be 3 characters.";
        }

        if (!form.currency_name.trim()) {
            newErrors.currency_name =
                "Currency name is required.";
        }

        if (
            form.exchange_rate === "" ||
            Number(form.exchange_rate) < 0
        ) {
            newErrors.exchange_rate =
                "Exchange rate must be a valid number.";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        const payload = {
            ...form,

            country_name:
                form.country_name.trim(),

            country_code:
                form.country_code.toUpperCase(),

            currency_code:
                form.currency_code.toUpperCase(),

            currency_name:
                form.currency_name.trim(),

            currency_symbol:
                form.currency_symbol.trim(),

            exchange_rate:
                Number(form.exchange_rate),

            is_active:
                Boolean(form.is_active),

            is_default:
                Boolean(form.is_default),

            is_manual_rate:
                Boolean(form.is_manual_rate),
        };

        await onSubmit(payload);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="space-y-5"
        >
            <div>
                <Typography
                    variant="small"
                    className="mb-2 font-medium"
                >
                   {t('country_name')}
                </Typography>

                <Input
                    label={t('country_name')}
                    value={form.country_name}
                    onChange={(e) =>
                        handleChange(
                            "country_name",
                            e.target.value
                        )
                    }
                    error={
                        Boolean(
                            errors.country_name
                        )
                    }
                />

                {errors.country_name && (
                    <Typography
                        variant="small"
                        color="red"
                        className="mt-1"
                    >
                        {errors.country_name}
                    </Typography>
                )}
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                {/* Country Code */}

                <div>
                    <Typography
                        variant="small"
                        className="mb-2 font-medium"
                    >
                        {t('country_code')}
                    </Typography>

                    <div className="flex items-center gap-3">

                        <Input
                            label="IN"
                            maxLength={2}
                            value={form.country_code}
                            onChange={(e) =>
                                handleCountryCodeChange(
                                    e.target.value
                                )
                            }
                            error={
                                Boolean(
                                    errors.country_code
                                )
                            }
                        />

                        {form.country_code && (
                            <span className="text-3xl">
                                {getFlag(
                                    form.country_code
                                )}
                            </span>
                        )}

                    </div>

                    {errors.country_code && (
                        <Typography
                            variant="small"
                            color="red"
                            className="mt-1"
                        >
                            {errors.country_code}
                        </Typography>
                    )}
                </div>

                {/* Currency Code */}

                <div>
                    <Typography
                        variant="small"
                        className="mb-2 font-medium"
                    >
                        {t('currency_code')}
                    </Typography>

                    <Input
                        label="USD"
                        maxLength={3}
                        value={form.currency_code}
                        onChange={(e) =>
                            handleChange(
                                "currency_code",
                                e.target.value
                                    .replace(
                                        /[^a-zA-Z]/g,
                                        ""
                                    )
                                    .slice(0, 3)
                                    .toUpperCase()
                            )
                        }
                        error={
                            Boolean(
                                errors.currency_code
                            )
                        }
                    />

                    {errors.currency_code && (
                        <Typography
                            variant="small"
                            color="red"
                            className="mt-1"
                        >
                            {errors.currency_code}
                        </Typography>
                    )}
                </div>

            </div>

            {/* Currency Name */}

            <div>
                <Typography
                    variant="small"
                    className="mb-2 font-medium"
                >
                    {t('currency_name')}
                </Typography>

                <Input
                    label={t('us_dollar')}
                    value={form.currency_name}
                    onChange={(e) =>
                        handleChange(
                            "currency_name",
                            e.target.value
                        )
                    }
                    error={
                        Boolean(
                            errors.currency_name
                        )
                    }
                />

                {errors.currency_name && (
                    <Typography
                        variant="small"
                        color="red"
                        className="mt-1"
                    >
                        {errors.currency_name}
                    </Typography>
                )}
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                {/* Currency Symbol */}

                <div>
                    <Typography
                        variant="small"
                        className="mb-2 font-medium"
                    >
                        {t('currency_symbol')}
                    </Typography>

                    <Input
                        label="$"
                        value={form.currency_symbol}
                        onChange={(e) =>
                            handleChange(
                                "currency_symbol",
                                e.target.value
                            )
                        }
                    />
                </div>

                {/* Exchange Rate */}

                <div>
                    <Typography
                        variant="small"
                        className="mb-2 font-medium"
                    >
                        {t('exchange_rate')}
                    </Typography>

                    <Input
                        label="1.000000"
                        type="number"
                        step="0.00000001"
                        min="0"
                        value={form.exchange_rate}
                        onChange={(e) =>
                            handleChange(
                                "exchange_rate",
                                e.target.value
                            )
                        }
                        error={
                            Boolean(
                                errors.exchange_rate
                            )
                        }
                    />

                    {errors.exchange_rate && (
                        <Typography
                            variant="small"
                            color="red"
                            className="mt-1"
                        >
                            {errors.exchange_rate}
                        </Typography>
                    )}
                </div>

            </div>

            {/* Flag */}

            <div>
                <Typography
                    variant="small"
                    className="mb-2 font-medium"
                >
                    {t('flag')}
                </Typography>

                <div className="
                    flex
                    h-12
                    items-center
                    gap-3
                    rounded-md
                    border
                    border-blue-gray-200
                    px-3
                ">

                    <span className="text-2xl">
                        {getFlag(
                            form.country_code
                        )}
                    </span>

                    <Typography>
                        {form.country_code
                            ? `${t('flag_for')} ${form.country_code}`
                            : "Enter country code"}
                    </Typography>

                </div>
            </div>

            {/* Active */}

            <div className="
                rounded-lg
                border
                border-blue-gray-100
                p-4
            ">

                <Switch
                    label={t('active_currency')}
                    checked={form.is_active}
                    onChange={(e) =>
                        handleChange(
                            "is_active",
                            e.target.checked
                        )
                    }
                />

                <Typography
                    variant="small"
                    color="gray"
                    className="ml-10"
                >
                   {t('active_currency_description')}
                </Typography>

            </div>

            {/* Manual Rate */}

            <div className="
                rounded-lg
                border
                border-blue-gray-100
                p-4
            ">

                <Switch
                    label={t('manual_exchange_rate')}
                    checked={form.is_manual_rate}
                    onChange={(e) =>
                        handleChange(
                            "is_manual_rate",
                            e.target.checked
                        )
                    }
                />

                <Typography
                    variant="small"
                    color="gray"
                    className="ml-10"
                >
                   {t('manual_exchange_rate_description')}
                </Typography>

            </div>

            {/* Default */}

            <div className="
                rounded-lg
                border
                border-blue-gray-100
                p-4
            ">

                <Switch
                    label={t('set_as_default_currency')}
                    checked={form.is_default}
                    onChange={(e) =>
                        handleChange(
                            "is_default",
                            e.target.checked
                        )
                    }
                />

                <Typography
                    variant="small"
                    color="gray"
                    className="ml-10"
                >
                 {t('set_default_currency_warning')}
                </Typography>

            </div>

            {/* Buttons */}

            <div className="
                flex
                justify-end
                gap-3
                border-t
                pt-5
            ">

                <Button
                    type="button"
                    variant="text"
                    onClick={onCancel}
                    disabled={loading}
                >
                    {t('cancel')}
                </Button>

                <Button
                    type="submit"
                    disabled={loading}
                >
                    {loading
                        ? t('saving')
                        : isEdit
                            ? t('update_currency')
                            : t('add_currency')
                    }
                </Button>

            </div>

        </form>
    );
}